import {
  LOCAL_PREFS_IMPORT_FLAG_KEY,
  USER_SCOPE,
  ganttViewPrefKey,
  portfolioScope,
  projectScope,
  viewColumnsPrefKey,
} from "./userPreferencesApi";

/**
 * SPEC-0061 §4.6 — every preference key family declares its scope ONCE, here.
 *
 *   user       — a way of working, valid on every portfolio of the tenant
 *                (column layouts, Gantt view settings, editing affordances).
 *   portfolio  — values that are portfolio data (project / resource ids, tags,
 *                attribute values) and date presets: never carried into another
 *                portfolio.
 *   project    — project dashboards and project-only filters (SPEC-0060).
 *
 * Every family reads through the userPreferences client: its localStorage keys
 * are imported once by localPreferencesImport, then never read locally again.
 *
 * `legacy` lists the localStorage keys the family imports: exact `keys`,
 * `prefixes`, `suffixes`. A legacy key must match exactly one family.
 * `legacy.perUser` marks keys suffixed by the account (`<key>-<sub>`): only the
 * current user's entry is imported, under the key without its suffix.
 * `prefKeyFor(id)` families keep one document per id (grid, view).
 */
export const SCOPE_KIND = Object.freeze({
  USER: "user",
  PORTFOLIO: "portfolio",
  PROJECT: "project",
});

const family = (name, definition) => {
  if (!definition.scope) throw new Error(`preference family "${name}" has no scope`);
  return Object.freeze({ name, legacy: {}, ...definition });
};

export const PREFERENCE_FAMILIES = Object.freeze({
  migration: family("migration", {
    scope: SCOPE_KIND.USER,
    prefKey: LOCAL_PREFS_IMPORT_FLAG_KEY,
  }),
  gantt_view: family("gantt_view", {
    scope: SCOPE_KIND.USER,
    prefKey: ganttViewPrefKey("scenario"),
    legacy: { keys: ["ganttScenarioFilters-V6"] },
  }),
  view_columns: family("view_columns", {
    scope: SCOPE_KIND.USER,
    // one document per view id: view_columns:<viewId>, optional portfolio version
    prefKeyFor: viewColumnsPrefKey,
    legacy: { prefixes: ["view_prefs."] },
  }),
  task_focus_columns: family("task_focus_columns", {
    scope: SCOPE_KIND.USER,
    prefKey: viewColumnsPrefKey("task_focus"),
    legacy: { keys: ["taskFocusGanttColumns"] },
  }),
  multi_project_editing: family("multi_project_editing", {
    scope: SCOPE_KIND.USER,
    prefKey: "multi_project_editing",
    legacy: { keys: ["multiProjectEditingEnabled-V1"] },
  }),
  projects_grid_filters: family("projects_grid_filters", {
    scope: SCOPE_KIND.USER,
    prefKey: "filters:projects_grid",
    legacy: { keys: ["Projects_Grid_Archived_Filter"], prefixes: ["projectDashboardFilter-"], perUser: true },
  }),
  date_presets: family("date_presets", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:date_presets",
    legacy: {
      keys: [
        "Portfolio_Date_Filter",
        "User_Taskboard_Date_Filter",
        "fullTimelineYearFilter-V2",
        "customTimelineYearFilter-V2",
        "customSupplyYearFilter",
        "scheduler_filter_dates",
      ],
    },
  }),
  scheduler_filters: family("scheduler_filters", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:scheduler",
    legacy: { prefixes: ["scheduler_filter_"] },
  }),
  task_focus_filters: family("task_focus_filters", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:task_focus",
    legacy: { keys: ["GANTT_TASK_FOCUS_FILTERS"] },
  }),
  taskboard_filters: family("taskboard_filters", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:taskboard",
    legacy: {
      keys: ["User_Taskboard_Team_Filter", "User_Taskboard_Late_Filter", "User_Taskboard_View"],
    },
  }),
  workload_filters: family("workload_filters", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:workload",
    legacy: { keys: ["selectedProjects", "selectedResources", "selectedYears"] },
  }),
  timeline_filters: family("timeline_filters", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:timeline",
    legacy: {
      keys: ["timelineFilters-V2"],
      suffixes: ["ProjectsFilter-V2", "TagsFilter-V2"],
    },
  }),
  shift_filters: family("shift_filters", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "filters:shifts",
    legacy: { keys: ["selected-shifts"] },
  }),
  // The user's copy of a « custom layout » grid (SET-06): one document per grid
  // id { columns, savedAt, visibleIds, source }. The admin's layout is the
  // published version (the look-ahead layout, server-sourced timestamp).
  legacy_grid_columns: family("legacy_grid_columns", {
    scope: SCOPE_KIND.USER,
    prefKeyFor: (gridId) => `grid_columns:${String(gridId).replace(/[^A-Za-z0-9_-]/g, "_")}`,
    legacy: {
      keys: [
        "myTasksSavedColumnsOrder",
        "taskFocusSavedColumnsOrder",
        "taskFocusColumnsSavedAt",
      ],
      suffixes: ["_columnsState", "_columnsSavedAt"],
    },
  }),
  // ---- view settings (a way of working: user scope, SPEC-0061 §4.6) ----
  task_focus_view: family("task_focus_view", {
    scope: SCOPE_KIND.USER,
    prefKey: ganttViewPrefKey("task_focus"),
    legacy: {
      keys: [
        "showResourceName",
        "showCustomColumnsTooltipOnTaskFocusGantt",
        "showCoreTooltipOnTaskFocusGantt",
        "TASKFOCUS_SNAP_FEATURE_KEY",
        "TASKFOCUS_TIME_RESOLUTION",
        "TASKFOCUS_SHOW_NON_WORKING_TIME",
      ],
    },
  }),
  // "Hide descriptions" of the dashboard panels (VIS-11): a way of reading, one for the tenant.
  analytics_view: family("analytics_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:analytics",
  }),
  taskboard_view: family("taskboard_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:taskboard",
    legacy: { keys: ["SHOW_USER_TASKBOARD_RESOURCE_UTILIZATION"] },
  }),
  custom_columns: family("custom_columns", {
    scope: SCOPE_KIND.USER,
    prefKey: "custom_columns:selected",
    legacy: {
      keys: [
        "SELECTED_TASK_FOCUS_GANTT_CUSTOM_COLUMNS",
        "SELECTED_FULL_TIMELINE_CUSTOM_COLUMNS",
        "SELECTED_PROGRAM_GANTT_CUSTOM_COLUMNS",
        "SELECTED_CUSTOM_WORKLOAD_CUSTOM_COLUMNS",
      ],
    },
  }),
  roadmap_view: family("roadmap_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:roadmap",
    legacy: { prefixes: ["roadmapGranularity-V1", "roadmapZoomFit-V1", "roadmapAllTagsOnRow-V1", "roadmapShowNames-V1"] },
  }),
  // The roadmap's collapsed and "all tags on one line" rows (PDR-0216): paths of group names, so
  // portfolio data, one entry per host screen.
  roadmap_rows: family("roadmap_rows", {
    scope: SCOPE_KIND.PORTFOLIO,
    prefKey: "view_settings:roadmap_rows",
  }),
  scheduler_view: family("scheduler_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:scheduler",
    legacy: { keys: ["activateTaskOnAssignValue"], suffixes: ["_vertical-mode", "-scheduler-settings"] },
  }),
  timeline_view: family("timeline_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:timeline",
    legacy: { keys: ["fullTimelineBaseline", "columnsForTimeline-V9", "timelineGrouper-V2", "timelineParameters-V2", "useCustomMultiProjectGantt-V1"] },
  }),
  workload_view: family("workload_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:workload",
    legacy: { keys: ["toggleState"] },
  }),
  scenario_workspace_view: family("scenario_workspace_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:scenario_workspace",
    legacy: { keys: ["scenarioWorkspace_topPane", "scenarioWorkspace_bottomPane", "scenario_workspace_kpiBarOpen"] },
  }),
  generative_workspace_view: family("generative_workspace_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:generative_workspace",
    legacy: {
      keys: [
        "gsep_setting_metricsDefaultOpen",
        "gsep_setting_workloadDefaultOpen",
        "gsep_metricsOpen",
        "gsep_workloadCollapsed",
        "gsep_bottomView",
      ],
      prefixes: ["gsep_viewMode_"],
    },
  }),
  scenarios_grid_view: family("scenarios_grid_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:scenarios_grid",
    legacy: { keys: ["validateAllScenariosOnPortfolioScenarioCreation"] },
  }),
  notifications_view: family("notifications_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:notifications",
    legacy: { keys: ["offolio.progressStack.collapsed"] },
  }),
  hints: family("hints", {
    scope: SCOPE_KIND.USER,
    prefKey: "hints:seen",
    legacy: { keys: ["analytics_only_you_tip_seen"] },
  }),
  visual_planning_view: family("visual_planning_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:visual_planning",
    legacy: { keys: ["VisualPlanningViewModeEnabled"] },
  }),
  library_view: family("library_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:library",
    legacy: { keys: ["Library_View"] },
  }),
  audit_log_view: family("audit_log_view", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:audit_log",
    legacy: { keys: ["AuditLogPage_View"] },
  }),
  grid_paging: family("grid_paging", {
    scope: SCOPE_KIND.USER,
    prefKey: "view_settings:grid_paging",
    legacy: { suffixes: ["_PageSize", "_CurrentPage"] },
  }),
  // The local key stays as the login screen's cache (read before any session
  // exists); after login the server document is the one applied.
  language: family("language", {
    scope: SCOPE_KIND.USER,
    prefKey: "language",
    legacy: { keys: ["i18n-lang"], keepLocal: true },
  }),
});

export const familyFor = (name) => {
  const found = PREFERENCE_FAMILIES[name];
  if (!found) throw new Error(`preference family "${name}" has no scope: declare it in userPreferences.registry.js`);
  return found;
};

const matchesLegacy = ({ keys = [], prefixes = [], suffixes = [] }, localKey) =>
  keys.includes(localKey) ||
  prefixes.some((p) => localKey.startsWith(p)) ||
  suffixes.some((s) => localKey.endsWith(s));

/** The family a legacy localStorage key belongs to, or null for a non-preference key. */
export const legacyFamilyFor = (localKey) => {
  // Exact keys win over prefix/suffix families (scheduler_filter_dates is a date preset).
  const exact = Object.values(PREFERENCE_FAMILIES).find((f) => (f.legacy.keys || []).includes(localKey));
  if (exact) return exact;
  return Object.values(PREFERENCE_FAMILIES).find((f) => matchesLegacy(f.legacy, localKey)) || null;
};

/** The server scope string of a family in the current context, or null when the context lacks the id. */
export const resolveScope = (fam, { portfolioId, projectId } = {}) => {
  if (fam.scope === SCOPE_KIND.USER) return USER_SCOPE;
  if (fam.scope === SCOPE_KIND.PORTFOLIO) return portfolioId != null ? portfolioScope(portfolioId) : null;
  return projectId != null ? projectScope(projectId) : null;
};
